// Converts legacy 'tablesheet' nodes into @lexical/table's 'table' > 'tablerow' > 'tablecell' tree.
//
// 'tablesheet' was a custom DecoratorNode that stored each cell as a serialised nested editor state
// string. That plugin has been removed, so this is the only thing that can still read such content -
// old messageRTF in the database is never rewritten unless a record is edited.
//
// Pure JSON -> JSON: no editor instance required, so it runs on stored messageRTF content before
// the editor state is parsed. See docs/code/lexical-table-migration.md

// Mirrors TableCellHeaderStates from @lexical/table. Kept local so this module stays dependency-free
// and testable without a DOM - the values are part of the serialised format and do not change.
const HEADER_NO_STATUS  = 0
const HEADER_ROW        = 1
const HEADER_COLUMN     = 2

const LEGACY_TYPE = 'tablesheet'

type LegacyCell = {
    colSpan?    : number
    json?       : string
    type?       : 'normal' | 'header'
    width?      : number | null
}

type LegacyRow = {
    cells?  : LegacyCell[]
    height? : number | null
}

const debug = false
// const debug = "migrateLegacyTables"

// TableCellNode.canBeEmpty() is false - every cell needs at least one block child
const createEmptyParagraph = () => {
    return {
        children    : [],
        direction   : null,
        format      : '',
        indent      : 0,
        type        : 'paragraph',
        version     : 1,
    }
}

// Legacy cells hold a whole serialised editor state as a string. Lift its top-level children out.
const getCellChildren = (cell: LegacyCell) => {
    let children: any[] = []

    try {
        const parsed = JSON.parse(cell.json || '')
        children = parsed?.root?.children || []
    } catch (e) {
        debug && console.log(debug, 'unparseable cell json', cell.json)
        children = []
    }

    if (_.isEmpty(children)) {
        return [createEmptyParagraph()]
    }

    return children
}

// $createTableNodeWithDimensions marked row 0 AND column 0 as 'header', which maps onto a bitmask
// rather than a boolean. A header cell in neither position still needs a non-zero state.
const getHeaderState = (cell: LegacyCell, x: number, y: number) => {
    if (cell.type !== 'header') {
        return HEADER_NO_STATUS
    }

    let state = HEADER_NO_STATUS
    if (y === 0) {
        state |= HEADER_ROW
    }
    if (x === 0) {
        state |= HEADER_COLUMN
    }

    return state === HEADER_NO_STATUS ? HEADER_ROW : state
}

const convertCell = (cell: LegacyCell, x: number, y: number) => {
    const converted: any = {
        children    : getCellChildren(cell),
        direction   : 'ltr',
        format      : '',
        indent      : 0,
        type        : 'tablecell',
        version     : 1,
        headerState : getHeaderState(cell, x, y),
        colSpan     : cell.colSpan || 1,
        rowSpan     : 1,
    }

    if (!_.isNil(cell.width)) {
        converted.width = cell.width
    }

    return converted
}

// @lexical/table assumes a rectangular grid - ragged rows break $computeTableMap, so pad short rows
const padCell = (): LegacyCell => {
    return {
        colSpan : 1,
        json    : '',
        type    : 'normal',
        width   : null,
    }
}

const convertRow = (row: LegacyRow, y: number, columnCount: number) => {
    const cells     = row.cells || []
    const padded    = _.times(columnCount, index => {
        return cells[index] || padCell()
    })

    const converted: any = {
        children    : _.map(padded, (cell, x) => {
            return convertCell(cell, x, y)
        }),
        direction   : 'ltr',
        format      : '',
        indent      : 0,
        type        : 'tablerow',
        version     : 1,
    }

    if (!_.isNil(row.height)) {
        converted.height = row.height
    }

    return converted
}

const convertTable = (node: any) => {
    const rows          = node.rows || []
    const columnCount   = _.max(_.map(rows, (row: LegacyRow) => {
        return (row.cells || []).length
    })) || 0

    debug && console.log(debug, 'convertTable', rows.length, columnCount)

    return {
        children    : _.map(rows, (row: LegacyRow, y: number) => {
            return convertRow(row, y, columnCount)
        }),
        direction   : 'ltr',
        format      : '',
        indent      : 0,
        type        : 'table',
        version     : 1,
    }
}

const migrateNode = (node: any): any => {
    if (!_.isPlainObject(node)) {
        return node
    }

    if (node.type === LEGACY_TYPE) {
        // TableNode.canBeEmpty() is false, so a rowless legacy table has no valid representation.
        // Replace it with an empty paragraph rather than emitting a table Lexical will reject.
        if (_.isEmpty(node.rows)) {
            debug && console.log(debug, 'dropping rowless legacy table')
            return createEmptyParagraph()
        }

        return convertTable(node)
    }

    if (_.isArray(node.children)) {
        return {
            ...node,
            children: _.map(node.children, migrateNode)
        }
    }

    return node
}

export function migrateLegacyTables(content: any) {
    if (!content?.root) {
        return content
    }

    return {
        ...content,
        root: migrateNode(content.root)
    }
}

export default migrateLegacyTables
